import { useEffect, useRef, useCallback, useState } from 'react'
import { getStoredTokens } from '@/store/authSlice'
import { store } from '@/store'
import { refreshToken } from '@/store/authSlice'

interface WebSocketNotification {
  id: string
  notification_type: string
  notification_type_display?: string
  message: string
  extra_data: Record<string, any>
  is_read: boolean
  created_at: string
}

interface UseNotificationWebSocketOptions {
  onNotification?: (notification: WebSocketNotification) => void
  enabled?: boolean
}

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error'

function getWebSocketUrl(): string {
  const baseUrl = import.meta.env.VITE_API_BASE_URL
    || `${window.location.protocol}//${window.location.host}`
  const wsUrl = baseUrl
    .replace(/^https:\/\//, 'wss://')
    .replace(/^http:\/\//, 'ws://')
  return `${wsUrl}/ws/notifications/`
}

export function useNotificationWebSocket({
  onNotification,
  enabled = true,
}: UseNotificationWebSocketOptions = {}) {
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5
  const reconnectDelay = 3000
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isRefreshingTokenRef = useRef(false)

  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')
  const [unreadCount, setUnreadCount] = useState(0)
  const [lastNotification, setLastNotification] = useState<WebSocketNotification | null>(null)

  const connect = useCallback(() => {
    if (!enabled) return

    // 이미 연결 중이거나 연결된 상태면 새로운 연결 시도 안함
    if (wsRef.current?.readyState === WebSocket.CONNECTING ||
        wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    const { accessToken } = getStoredTokens()
    if (!accessToken) {
      console.warn('WebSocket 연결 실패: 액세스 토큰이 없습니다')
      setConnectionStatus('disconnected')
      return
    }

    setConnectionStatus('connecting')

    try {
      const baseUrl = getWebSocketUrl()
      const url = `${baseUrl}?token=${encodeURIComponent(accessToken)}`
      const ws = new WebSocket(url)
      wsRef.current = ws

      ws.onopen = () => {
        console.log('알림 WebSocket 연결됨')
        setConnectionStatus('connected')
        reconnectAttemptsRef.current = 0
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)

          switch (data.type) {
            case 'connection_established':
              console.log(data.message)
              break

            case 'auth_error':
              // 인증 실패 → 토큰 갱신 후 재연결
              console.error('WebSocket 인증 실패:', data.message)
              handleAuthFailure()
              break

            case 'notification': {
              const notification: WebSocketNotification = data.data
              setLastNotification(notification)

              // 읽지 않은 알림이면 카운트 증가
              if (!notification.is_read) {
                setUnreadCount((prev) => prev + 1)
              }

              // 콜백 호출
              onNotification?.(notification)
              break
            }

            case 'pong':
              // ping 응답 - 연결 유지 확인
              break

            default:
              console.log('Unknown WebSocket message type:', data.type)
          }
        } catch (err) {
          console.error('WebSocket 메시지 파싱 오류:', err)
        }
      }

      ws.onclose = (event) => {
        console.log('알림 WebSocket 연결 해제:', event.code, event.reason)
        setConnectionStatus('disconnected')
        wsRef.current = null

        // ping interval 정리
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current)
          pingIntervalRef.current = null
        }

        // 인증 실패(4001)로 인한 종료 → 토큰 갱신 후 재연결
        if (event.code === 4001) {
          handleAuthFailure()
          return
        }

        // 재연결 시도 (정상 종료가 아닌 경우에만)
        if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts && enabled) {
          reconnectAttemptsRef.current++
          console.log(`알림 WebSocket 재연결 시도 ${reconnectAttemptsRef.current}/${maxReconnectAttempts}...`)
          setTimeout(connect, reconnectDelay)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket 오류:', error)
        setConnectionStatus('error')
      }
    } catch (err) {
      console.error('WebSocket 연결 생성 오류:', err)
      setConnectionStatus('error')
    }
  }, [enabled, onNotification])

  // 인증 실패 시 토큰 갱신 후 재연결
  const handleAuthFailure = useCallback(async () => {
    // 이미 토큰 갱신 중이면 중복 실행 방지
    if (isRefreshingTokenRef.current) return
    isRefreshingTokenRef.current = true

    try {
      const result = await store.dispatch(refreshToken()).unwrap()
      if (result) {
        // 토큰 갱신 성공 → 새 토큰으로 재연결
        reconnectAttemptsRef.current = 0
        connect()
      }
    } catch {
      // 토큰 갱신 실패 → 재연결 중단 (로그아웃은 authSlice에서 처리)
      console.error('WebSocket 토큰 갱신 실패: 재연결을 중단합니다')
      setConnectionStatus('error')
    } finally {
      isRefreshingTokenRef.current = false
    }
  }, [connect])

  const disconnect = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }

    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnect')
      wsRef.current = null
    }

    setConnectionStatus('disconnected')
    reconnectAttemptsRef.current = maxReconnectAttempts // 재연결 방지
  }, [])

  const sendPing = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }))
    }
  }, [])

  // 연결/해제 관리
  useEffect(() => {
    if (enabled) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [enabled]) // connect, disconnect를 의존성에서 제외하여 무한 루프 방지

  // 주기적 ping (연결 유지) - 30초마다
  useEffect(() => {
    if (connectionStatus !== 'connected') return

    pingIntervalRef.current = setInterval(() => {
      sendPing()
    }, 30000)

    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current)
        pingIntervalRef.current = null
      }
    }
  }, [connectionStatus, sendPing])

  return {
    connectionStatus,
    isConnected: connectionStatus === 'connected',
    unreadCount,
    setUnreadCount,
    lastNotification,
    disconnect,
    reconnect: connect,
  }
}

export type { WebSocketNotification, ConnectionStatus }
